"use client"

import { NavbarProps } from "@/components/fragments/Navbar"
import { ThemeToggle } from "@/components/theme-toggle"
import { Button } from "@/components/ui/button"
import { gsap } from "gsap"
import { MenuSquareIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"

export default function VerticalToolTipMenu({ activeSection, scrollToSection, navItems }: NavbarProps) {
    const [isHovered, setIsHovered] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const bubbleRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const itemsRef = useRef<HTMLDivElement>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const tl = useRef<gsap.core.Timeline | null>(null)

    // Handle scroll visibility for "Scroll to Top"
    useEffect(() => {
        const toggleVisibility = () => {
        }
        window.addEventListener("scroll", toggleVisibility)
        return () => window.removeEventListener("scroll", toggleVisibility)
    }, [])

    // Initialize GSAP Timeline
    useEffect(() => {
        if (!bubbleRef.current || !itemsRef.current) return

        tl.current = gsap.timeline({ paused: true })

        // Bubble liquid/morph animation
        tl.current.to(bubbleRef.current, {
            scale: 1.2,
            borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
            duration: 0.6,
            ease: "power2.inOut"
        }, 0)
            .to(bubbleRef.current, {
                scale: 1.5,
                borderRadius: "50% 50% 50% 50%",
                duration: 0.4,
                ease: "power2.out"
            }, 0.6)

        // Menu items expansion
        tl.current.fromTo(itemsRef.current,
            { opacity: 0, y: 20, scale: 0.8, pointerEvents: "none" },
            { opacity: 1, y: 0, scale: 1, pointerEvents: "auto", duration: 0.4, ease: "back.out(1.7)" },
            0.4
        )

        return () => {
            tl.current?.kill()
        }
    }, []) // Run once on mount

    // Handle Hover direction
    useEffect(() => {
        if (isHovered) {
            tl.current?.play()
        } else {
            tl.current?.reverse()
        }
    }, [isHovered])

    useEffect(() => {
        const trigger = containerRef.current
        if (!trigger) return

        const onMouseLeave = () => {
            gsap.to(trigger, {
                x: 0,
                y: 0,
                duration: 0.5,
                ease: "elastic.out(1, 0.3)"
            })
        }

        trigger.addEventListener("mouseleave", onMouseLeave)

        return () => {
            trigger.removeEventListener("mouseleave", onMouseLeave)
        }
    }, [])

    const handleMouseLeave = () => {
        // Add 1.5s delay before collapsing
        timeoutRef.current = setTimeout(() => {
            setIsHovered(false)
        }, 1500)
    }

    const onClick = () => {
        setIsHovered(!isHovered)
    }

    return (
        <div
            ref={menuRef}
            className="md:hidden fixed bottom-14 right-4 z-50 flex flex-col items-center"
            onClick={() => onClick()}
            onMouseLeave={handleMouseLeave}
        >
            {/* Expanded Menu Items */}
            <div
                ref={itemsRef}
                className="flex flex-col gap-3 p-2 mb-4 rounded-2xl bg-background/60 backdrop-blur-2xl border border-primary/20 shadow-[0_0_30px_rgba(var(--primary),0.1)] transition-colors duration-500"
                style={{ opacity: 0 }}
            >
                <div className="flex justify-center border-b border-emerald-500 pb-1">
                    <ThemeToggle />
                </div>

                {navItems.map((item) => (
                    <motion.button
                        key={item.name}
                        onClick={() => scrollToSection(item.ref)}
                        className={`relative px-1 py-2 text-lg font-medium transition-colors ${activeSection === item.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                            }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {item.name}
                        {activeSection === item.id && (
                            <motion.div
                                layoutId="activeSection"
                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                            />
                        )}
                    </motion.button>
                ))}
            </div>

            {/* Trigger Button with Bubble Effect */}
            <div
                ref={containerRef}
                className="relative flex items-center justify-center group"
            >
                <div
                    ref={bubbleRef}
                    className="absolute inset-0 bg-primary/30 rounded-full blur-xl group-hover:bg-primary/40 transition-colors duration-500"
                    style={{ transform: "scale(0)" }}
                />
                <Button
                    variant="outline"
                    size="icon"
                    className="relative z-10 w-14 h-14 rounded-full border-2 border-primary/30 bg-background/60 backdrop-blur-2xl shadow-2xl hover:border-primary/60 hover:bg-primary/10 transition-all duration-500 overflow-hidden"
                >
                    <MenuSquareIcon className="h-7 w-7 text-primary animate-pulse group-hover:animate-none" />
                </Button>
            </div>
        </div>
    )
}
